import { createContext, useContext, FC, useCallback, useEffect, useReducer } from 'react';
import toast from 'react-hot-toast';
import { defineMessages, useIntl } from 'react-intl';

const msgs = defineMessages({
  audioLoadError: {
    id: 'learning_session_media_error',
  },
});

type AudioObject = { [url: string]: HTMLAudioElement | undefined };

interface AudioState {
  playingAudioUrl: string | null;
  /** Mapping between src urls and HTMLAudioElements */
  audioObject: AudioObject;
}

export const initialAudioState = {
  playingAudioUrl: null,
  audioObject: {},
};

interface AudioContextValue extends AudioState {
  dispatch: React.Dispatch<AudioAction>;
}

const initialContextValue: AudioContextValue = {
  ...initialAudioState,
  dispatch: /* istanbul ignore next */ () => null,
};

type AddAudioUrlsAction = {
  type: 'ADD_AUDIO_URL';
  url: string;
};

type ResetPlayingAudioUrl = {
  type: 'RESET_PLAYING_AUDIO_URL';
};

type SetPlayingAudioUrl = {
  type: 'SET_PLAYING_AUDIO_URL';
  url: string;
};

type AudioAction = AddAudioUrlsAction | ResetPlayingAudioUrl | SetPlayingAudioUrl;

export const audioReducer = (state: AudioState, action: AudioAction): AudioState => {
  switch (action.type) {
    case 'ADD_AUDIO_URL': {
      const { url } = action;
      const { audioObject, ...restState } = state;
      // If no new url, return pre-existing state
      /* istanbul ignore if */
      if (audioObject[url]) return state;
      // Otherwise, add a new audio element

      return {
        ...restState,
        audioObject: {
          ...audioObject,
          [url]: new Audio(url),
        },
      };
    }
    case 'SET_PLAYING_AUDIO_URL': {
      return { ...state, playingAudioUrl: action.url };
    }
    case 'RESET_PLAYING_AUDIO_URL': {
      return { ...state, playingAudioUrl: null };
    }
  }
};

export const AudioContext = createContext<AudioContextValue>(initialContextValue);
const { Provider } = AudioContext;

type AudioProviderProps = {
  children: React.ReactNode;
};

export const AudioProvider: FC<AudioProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(audioReducer, initialAudioState);

  // Keep track of when audio elements stop playing
  useEffect(() => {
    /* istanbul ignore next */
    const handler = () => dispatch({ type: 'RESET_PLAYING_AUDIO_URL' });
    Object.values(state.audioObject).forEach(audio => audio?.addEventListener('ended', handler));
    return () => {
      Object.values(state.audioObject).forEach(audio =>
        audio?.removeEventListener('ended', handler),
      );
    };
  }, [state.audioObject, dispatch]);

  return <Provider value={{ ...state, dispatch }}>{children}</Provider>;
};

export function useAudioContext() {
  const ctx = useContext(AudioContext);
  /* istanbul ignore next: for tests only */
  if (!ctx) {
    throw new Error('You need to be inside a AudioProvider when using `useAudioContext`');
  }
  return ctx;
}

export const secondsToMs = (seconds: number): number => seconds * 1000;

/**
 * Returns the length of the audio clip for the given url
 */

/* istanbul ignore next */
export function useAudioLengthInMs(url: string | null): number | null {
  const { audioObject, dispatch } = useAudioContext();

  useEffect(() => {
    if (url === null) return;
    dispatch({ type: 'ADD_AUDIO_URL', url });
  }, [url, dispatch]);

  if (!url) return null;
  const audio = audioObject[url];
  if (!audio) return null;
  return secondsToMs(audio.duration);
}

export const isPlaying = ({ paused, ended, readyState }: HTMLAudioElement) =>
  !paused &&
  !ended &&
  // readyState indicates media load state
  // Values greater than two mean that enough data has loaded for the media to play
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState#value
  readyState > 2;

export const stopAudio = (el: HTMLAudioElement) => {
  el.pause();
  el.currentTime = 0;
};

/**
 * Stops all currently playing audio in an AudioObject
 *
 * Note that we could replace this function with a check to playingAudioUrl.
 * However, that would result in state dependencies that would lead to more
 * re-renders than necessary (or infinite loops when playingAudioUrl gets
 * set and then re-reset).
 */
export const stopPlayingAudio = (audioObj: AudioObject): void =>
  Object.values(audioObj)
    .filter(audio => audio && isPlaying(audio))
    .forEach(audio => audio && stopAudio(audio));

export function useAudioPlayback(url: string | null): {
  play: () => Promise<void>;
  stopAll: () => void;
  playing: boolean;
} {
  const { dispatch, audioObject, playingAudioUrl } = useAudioContext();
  const intl = useIntl();

  useEffect(() => {
    // istanbul ignore next: wasn't covered before, please test
    if (url === null) return;
    dispatch({ type: 'ADD_AUDIO_URL', url });
  }, [url, dispatch]);

  // istanbul ignore next: wasn't covered before, please test
  const stopAll = useCallback((): void => {
    stopPlayingAudio(audioObject);
    dispatch({ type: 'RESET_PLAYING_AUDIO_URL' });
  }, [audioObject, dispatch]);

  /* istanbul ignore next */
  const play = useCallback(async (): Promise<void> => {
    if (url === null) return;
    stopPlayingAudio(audioObject);
    const { [url]: toPlay } = audioObject;
    if (!toPlay) return;
    try {
      // await for play success
      await toPlay.play();
      dispatch({ type: 'SET_PLAYING_AUDIO_URL', url });
    } catch (e) /* istanbul ignore next: not expected */ {
      toPlay.load();
      const { name } = e as Error;
      const safariAutoplayWarning = name === 'AbortError' || name === 'NotSupportedError';
      const chromeOrFirefoxAutoplayWarning = name === 'NotAllowedError';
      if (safariAutoplayWarning || chromeOrFirefoxAutoplayWarning) {
        return;
      }
      // tells audioElement to retry on the chance it reloads
      toast.error(intl.formatMessage(msgs.audioLoadError));
      // Throw if error is unexpected
      throw e;
    }
  }, [audioObject, dispatch, url, intl]);

  const playing = url === playingAudioUrl;

  return { play, playing, stopAll };
}
